import { Component, ViewChild, ChangeDetectorRef, Input, ElementRef, LOCALE_ID, Inject, Optional } from '@angular/core';
import { ModalsComponent } from '../modals.component';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { forEach, flatten, groupBy, get, maxBy, uniq, intersectionBy, differenceBy, map, orderBy, sortedLastIndexBy, sortedIndexBy } from 'lodash-es';
import { ngfSelect } from 'angular-file';
import { iif, of, Subscription } from 'rxjs';
import { finalize, debounceTime, delay, tap } from 'rxjs/operators';

import { GroupService } from '@schoolbelle/api/group';
import { LetterTargetService } from '@schoolbelle/api/letter-target';
import { TreeService } from '@schoolbelle/api/group-to-group';
import { GroupMember, GroupMemberService } from '@schoolbelle/api/group-member';
import { ExcelService, ExcelWorkBookData } from '@schoolbelle/api/excel';
import { LoadingService } from '@schoolbelle/common/loading';
import { DialogService } from '@schoolbelle/common/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationService } from '@schoolbelle/common/translation';
import { sprintf } from 'sprintf-js';



@Component({
  selector: 'modal-select-letter-targets',
  templateUrl: './select-letter-targets-modal.component.html',
  styleUrls: ['./select-letter-targets-modal.component.scss'],
})
export class SelectLetterTargetsModalComponent extends ModalsComponent {
  @ViewChild(ngfSelect) ngfSelect:ngfSelect;
  @Input() tab:'studentparent'|'faculty' = 'studentparent';
  get toClass ():boolean {
    const target = this.target.targets[0];
    return this.target.targets.length === 1 && target.scopes === 'studentparent_all';
  }
  set toClass (v:boolean) {
    if (this.toClass === v) return;
    if (v === true) {
      this.target.unload();
      this.target.setScopes(this.group.currentGroup.gid, 'studentparent_all');
    }
    else {
      this.target.unload();
      this.target.setScopes(this.group.currentGroup.gid, 'studentparent_none');
    }
  }
  get toAdmins ():boolean {
    return !this.toClass;
  }
  set toAdmins (v:boolean) {
    this.toClass = !v;
  }
  uploaded:File;
  private subscriptions = new Subscription();
  constructor (
    @Optional() public modalRef:BsModalRef,
    public group:GroupService,
    public target:LetterTargetService,
    private excel:ExcelService,
    private tree:TreeService,
    private member:GroupMemberService,
    private loading:LoadingService,
    private dialogs:DialogService,
    private toastr:ToastrService,
    private cdRef:ChangeDetectorRef,
    private elementRef:ElementRef,
    private translation:TranslationService,
    @Inject(LOCALE_ID) private locale_id:string,
  ) {
    super(modalRef);
  }


  hasAccessToMemberlist:boolean = false; 
  ngOnInit () {
    this.hasAccessToMemberlist = this.group.checkPermission('admin')  || this.group.checkPermission('faculty')
    super.ngOnInit();

    this.subscriptions.add(
      this.target.onChange.pipe(
        debounceTime(0),
      )
      .subscribe(()=>{
        this.cdRef.detectChanges();
      })
    );
  }
  ngOnDestroy () {
    this.subscriptions.unsubscribe();
  }
  
  onExcelUpload (file:File) {
    const errors = [];
    const subscription = this.excel.read(file)
    .pipe(
      finalize(()=>this.refreshFileInput())
    )
    .subscribe(data=>{
      this.target.load([]);
      const data_without_header = data.slice(1);
      const data_trimmed = data_without_header.map(row=>{
        let [group_name, member_num, member_name, target1, target2, target3] = row;
        if (group_name && typeof group_name === 'string') group_name = group_name.trim();
        if (member_name && typeof member_name === 'string') member_name = member_name.trim();
        if (member_num && typeof member_num === 'string') member_num = Number(member_num.trim());

        if (!group_name || !member_num || !member_name) return;
        if (this.tab === 'studentparent' && !target1 && !target2 && !target3) return;
        if (this.tab === 'faculty' && !target1) return;
        if (typeof member_num !== 'number') {
          errors.push(sprintf(this.translation.get('select-letter-targets-modal-wrong-number'), group_name, member_num, member_name, member_num));
          return;
        }
        return [group_name, member_num, member_name, target1, target2, target3];
      }).filter(v=>v);
      const grouped = groupBy(data_trimmed, row=>row[0]);
      const mapping = new Map();
      forEach(grouped, (data, group_name)=>{
        const targetGroup = this.tree.root.find({group_name});
        if (!targetGroup) {
          errors.push(sprintf(this.translation.get('select-letter-targets-modal-group-not-found'), group_name));
          return;
        }  
        const scopes = flatten(data.map(row=>{
          const [group_name, member_num, member_name, target1, target2, target3] = row;
          const list = [];
          const member = this.member.search({group_id:targetGroup.group_id, member_types:['student','faculty'], member_num:Number(member_num), member_name:member_name as string})[0];
          if (!member) {
            errors.push(sprintf(this.translation.get('select-letter-targets-modal-member-not-found'), group_name, member_num, member_name));
            return;
          }
          if (this.tab === 'faculty') {
            list.push(this.member.turnMembertoScope(member));
          }
          else {
            if (target3 && target1 && target2) list.push(this.member.turnMembertoScope(member));
            else {
              if (target3) list.push(this.member.turnMembertoScope(member, true));
              if (target1) list.push(this.member.turnMembertoScope({member_type:'parent', member_num:1, student_member_name:member.member_name}, true));
              if (target2) list.push(this.member.turnMembertoScope({member_type:'parent', member_num:2, student_member_name:member.member_name}, true));
              }
          }
          return list;            
        }))
        .filter(v=>v).join(',');
        mapping.set(targetGroup.group_id, scopes);
      })
      iif(()=>errors.length > 0, this.dialogs.confirm(null, errors.map(e=>`<div class="text-center py-1">${e}</div>`).join('') + `<div class="text-center py-2">${this.translation.get('select-letter-targets-modal-continue')}</div>`), of(true)).subscribe(bool=>{
        if (bool) {
          mapping.forEach((scopes, group_id)=>{
            this.target.setScopes(group_id, scopes);
          })
        }
      })
    
    },e=>{
      this.toastr.error(e.error || e.message);      
    })    
    this.loading.open({message:this.translation.get('select-letter-targets-modal-reading'), busy:subscription});
  }
  private refreshFileInput () {
    this.ngfSelect.files = [];
    const input = this.ngfSelect.element.nativeElement.querySelector('input');    
    if (input) input.value = null;
  }
  downloadAsExcel () {
    const sheets:ExcelWorkBookData = {};
    const data:(string|number)[][] = [
      this.tab === 'studentparent' ? [
        this.translation.get('select-letter-targets-modal-class'),
        this.translation.get('select-letter-targets-modal-number'),
        this.translation.get('select-letter-targets-modal-name'),
        this.translation.get('select-letter-targets-modal-parent1'),
        this.translation.get('select-letter-targets-modal-parent2'),
        this.translation.get('select-letter-targets-modal-student'),
      ] : [
        this.translation.get('select-letter-targets-modal-school'),
        this.translation.get('select-letter-targets-modal-number'),
        this.translation.get('select-letter-targets-modal-name'),
      ],
    ];

    // order
    const targets = this.target.targets;
    const targets_without_abstract_ones = targets.filter(target=>{
      const targetGroup = this.tree.root.find({group_id:target.group_id});
      if (this.tab === 'studentparent' && this.tree.depth !== 0 && targetGroup.depth !== 2) return false;
      return true;
    });
    const targets_ordered = this.target.orderTargetsWithGroupPriority(targets_without_abstract_ones);

    targets_ordered.forEach(target=>{
      const targetGroup = this.tree.root.find({group_id:target.group_id});
      if (this.tab === 'studentparent' && this.tree.depth !== 0 && targetGroup.depth !== 2) return;
      const memberlist = this.member.search({group_id:target.group_id, member_types:[this.tab === 'studentparent' ? 'student':'faculty']});
      memberlist.forEach(member=>{
        if (member.member_type === 'student') {
          let target1 = this.member.inScopes({group_id:member.group_id, member_type:'parent', member_num:1, student_member_name:member.member_name}, target.scopes);
          let target2 = this.member.inScopes({group_id:member.group_id, member_type:'parent', member_num:2, student_member_name:member.member_name}, target.scopes);
          let target3 = this.member.inScopes(member, target.scopes);
          const row = [member.group_name, member.member_num, member.member_name, target1?'v':'', target2?'v':'', target3?'v':''];
          if (row.slice(2).filter(v=>v).length === 0) return;
          data.push(row);
        } else {
          let target1 = this.member.inScopes(member, target.scopes);
          const row = [member.group_name, member.member_num, member.member_name, target1?'v':''];
          data.push(row);
        }
      })
      // .filter(v=>v)  

    });
    sheets['Sheet1'] = data;
    const ob = this.excel.write(sheets, `발행목록`);
    this.loading.open({message:this.translation.get('select-letter-targets-modal-downloading'), busy:ob.subscribe()});
  }  
  isAnyChecked () {
    if (this.target.targets.length === 0) return false;
    else return true;
  }

  isStudentChecked () {
    if (this.target.targets.length === 0) return false;
    const target_with_all_students_not_checked = this.target.targets.find(t=>{
      // abstact groups such as schools and grades will have scope values of this 
      // {group_id}.studentparent_{{group_scope}}
      if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
      const scopes = t.scopes.split(',');
      if (scopes.includes('all')) return false;
      if (scopes.includes('studentparent_all')) return false;
      if (scopes.includes('studentparent_col_1')) return false;

      /**
       * mumber_num = 2 should return true
       * n 1 2 3
       * a v v v
       * b - - v
       * - - - -
       * d - - v
       * 
       */
      const num_of_student = this.member.search({group_id:t.group_id, member_types:['student']}).length;
      if (scopes.filter(s=>s.match(`studentparent_col_1_of_row_`) || s.match(`studentparent_row_`)).length === num_of_student) return false;
      
      return true;
    });
    return target_with_all_students_not_checked ? false : true;
  }
  isParentChecked (member_num:number = undefined) {
    if (this.target.targets.length === 0) return false;
    const target_with_all_parents_not_checked = this.target.targets.find(t=>{
      if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
      const scopes = t.scopes.split(',');
      if (scopes.includes('all')) return false;
      if (scopes.includes('studentparent_all')) return false;

      if (typeof member_num === 'undefined') {
        if (scopes.includes('studentparent_col_2') && scopes.includes('studentparent_col_3')) return false;

      }
      else {
        if (scopes.includes(`studentparent_col_${member_num + 1}`)) return false;
      }
      /**
       * mumber_num = 2 should return true
       * n 1 2 3
       * a v v v
       * b - v -
       * - - - -
       * d - v -
       * 
       */
      const num_of_student = this.member.search({group_id:t.group_id, member_types:['student']}).length;
      if (typeof member_num === 'undefined') {  
        if (
          scopes.filter(s=>s.match('studentparent_col_2_of_row_') || s.match(`studentparent_row_`)).length === num_of_student 
          && scopes.filter(s=>s.match('studentparent_col_3_of_row_') || s.match(`studentparent_row_`)).length === num_of_student
        ) return false;

      }
      else {
        if (
          scopes.filter(s=>s.match(`studentparent_col_${member_num+1}_of_row_`) || s.match(`studentparent_row_`)).length === num_of_student 
        ) return false;
      }  
      return true;
    });
    return target_with_all_parents_not_checked ? false : true;
  }
  toggleStudent () {
    if (this.target.targets.length === 0) return;
    if (this.isStudentChecked()) { // set to false
      this.target.targets.forEach(t=>{
        if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
        this.target.setScopes(t.group_id, this.removeFromScopes(t.group_id, 'studentparent_col_1', t.scopes), 'both', false);
      });
    } 
    else { // set to true
      this.target.targets.forEach(t=>{
        if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
        this.target.setScopes(t.group_id, this.addToScopes(t.group_id, 'studentparent_col_1', t.scopes), 'both', false);
      });
    }
    this.target.refreshTargets();
    this.target.refreshSimplifiedTargets();

  }
  toggleParent (member_num:number = undefined) {
    if (this.target.targets.length === 0) return;
    const isParentChecked = this.isParentChecked(member_num);
    if (isParentChecked) { // set to false
      this.target.targets.forEach(t=>{
        if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
        if (typeof member_num === 'undefined') {
          this.target.setScopes(t.group_id, this.removeFromScopes(t.group_id, 'studentparent_col_2,studentparent_col_3', t.scopes), 'both', false);
        }
        else this.target.setScopes(t.group_id, this.removeFromScopes(t.group_id, `studentparent_col_${member_num + 1}`, t.scopes), 'both', false);
      });
    } 
    else { // set to true
      this.target.targets.forEach(t=>{
        if(t.scopes.match(/[\d+]\.studentparent_/)) return false;
        if (typeof member_num === 'undefined') this.target.setScopes(t.group_id, this.addToScopes(t.group_id, 'studentparent_col_2,studentparent_col_3', t.scopes), 'both', false);
        else this.target.setScopes(t.group_id, this.addToScopes(t.group_id, `studentparent_col_${member_num + 1}`, t.scopes), 'both', false);
      });
    }
    this.target.refreshTargets();
    this.target.refreshSimplifiedTargets();    
  }
  private hasScopes (group_id:number, scopesA:string, scopesB:string):0|1|2 {
    const x = 3;
    const y = get(maxBy(this.member.search({group_id:group_id, member_types:['student']}), 'member_num'), 'member_num');

    return this.member.helper.hasScopes( 
      this.member.convertNameScopesToNumberScopesFromList(scopesA, group_id, 'student'), 
      this.member.convertNameScopesToNumberScopesFromList(scopesB, group_id, 'student'),
      x, 
      y, 
      'studentparent_'
    );
  }
  
  private addToScopes (group_id:number, scopesA:string, scopesB:string):string {
    const x = 3;
    const y = get(maxBy(this.member.search({group_id, member_types:['student']}), 'member_num'), 'member_num');
    if (this.hasScopes(group_id, scopesA, scopesB) === 2) return scopesB;
    const added = this.member.convertNumberScopesToNameScopesFromList(
      this.member.helper.addToScopes(
        this.member.convertNameScopesToNumberScopesFromList(scopesA, group_id, 'student'), 
        this.member.convertNameScopesToNumberScopesFromList(scopesB, group_id, 'student'),
        x, 
        y, 
        'studentparent_'
      ),
      group_id,
    );
    return added;
  }
  private removeFromScopes (group_id:number, scopesA:string, scopesB:string):string {
    const x = 3;
    const y = get(maxBy(this.member.search({group_id, member_types:['student']}), 'member_num'), 'member_num');
    // if (this.hasScopes(group_id, scopesA, scopesB) !== 2) return scopesB;
    if (this.hasScopes(group_id, scopesA, scopesB) === 0) return scopesB;


    return this.member.convertNumberScopesToNameScopesFromList(
      this.member.helper.removeFromScopes(
        this.member.convertNameScopesToNumberScopesFromList(scopesA, group_id, 'student'), 
        this.member.convertNameScopesToNumberScopesFromList(scopesB, group_id, 'student'),
        x, 
        y, 
        'studentparent_'
      ),
      group_id,
    );  
  }

  selectPerHousehold () {
    const targeted_members = this.target.getMembersInTargets();
    
    if (targeted_members.length > 0) { // list selected
      // original logic
      const subscription = of(null).pipe(
        delay(500),
        tap(()=>this._selectPerHousehold())
      ).subscribe()
  
      this.loading.open({message:this.translation.get('select-letter-targets-modal-selecting-per-household'), busy:subscription});
    } else { // selected list not exists, show alert dialog
      alert(this.translation.get("alert-select-letter-targets-modal-selecting-per-household"));
    }
  }
  private _selectPerHousehold () {


    const time = Date.now();
    console.debug('selectPerHousehold');

    const isParent1Checked = this.isParentChecked(1);
    const isParent2Checked = this.isParentChecked(2);
    const isStudentChecked = this.isStudentChecked();

    console.debug(Date.now() - time,'selectPerHousehold', '3 isCheck\'s');
    const targeted_members = this.target.getMembersInTargets()

    // non-target students will be filtered out later.
    const students = this.member.list.filter(m=>m.member_type === 'student');
    const parents = targeted_members.filter(m=>m.member_type === 'parent');
    const parents_sorted = orderBy(parents, m=>m.student_member_id);
    console.debug(Date.now() - time,'selectPerHousehold', 'get students and parents');

    const households:GroupMember[][] = [];
    students.forEach(student=>{

      const start = sortedIndexBy(parents_sorted, {student_member_id:student.member_id} as any, m=>m.student_member_id);
      const end = sortedLastIndexBy(parents_sorted, {student_member_id:student.member_id} as any, m=>m.student_member_id);

      const parents_related = parents_sorted.slice(start, end);
      const members = [student, ...parents_related]
      .filter(m=>this.target.isMemberInTargets(m, targets)) 
      .filter(v=>v.member_phone_number);
      if (members.length === 0) return;

      let found:any = false;

      for (let i in households) {
        let household = households[i];
        const intersected = intersectionBy(household, members, (m=>m.member_phone_number));

        if (intersected.length > 0) {
          households[i] = [...household, ...differenceBy(members, household, m=>m.member_id)];
          found = households[i];
          break;
        }
      }
      if (!found) households.push(members); 
    });

    console.debug(Date.now() - time,'selectPerHousehold', 'build household list');


    const households_grouped_by_group_id = groupBy(
      households.map(list=>{
        // return minBy(list, m=>{
        return maxBy(list, m=>{
          const targetGroup = this.tree.root.find({group_id:m.group_id});
          return this.target.getPriority(targetGroup);
        });
      }), 
      m=>m && m.group_id
    );
    console.debug(Date.now() - time,'selectPerHousehold', 'group household list per group_id');

    const targets = map(households_grouped_by_group_id, (memberlist, group_id)=>{
    // forEach(households_grouped_by_group_id, (memberlist, group_id)=>{
      const scopes_in_array = uniq(
        memberlist.map(m=>{
          if (isParent1Checked && isParent2Checked && isStudentChecked) return this.member.turnMembertoScope(m, false);
          else if (isParent1Checked && isParent2Checked) {
            if (m.member_type !== 'parent') return;
            return [ this.member.turnMembertoScope({...m, member_num:1}, true), this.member.turnMembertoScope({...m, member_num:2}, true)].join(',')
          }
          else if (isParent1Checked) {
            if (m.member_type !== 'parent' || m.member_num !== 1) return;
            return this.member.turnMembertoScope({...m, member_num:1}, true);

          } 
          else if (isParent2Checked) {
            if (m.member_type !== 'parent' || m.member_num !== 2) return;
            return this.member.turnMembertoScope({...m, member_num:2}, true);
          }
        })
        .filter(v=>v)
      );    
      const scopes = scopes_in_array.join(',');
      return {group_id:Number(group_id), scopes};
    })
    console.debug(Date.now() - time,'selectPerHousehold', 'compuled targets');


    const targets_compressed = targets.map(({group_id, scopes})=>{
      return {
        group_id,
        scopes:this.member.compressScopes(scopes, group_id, 'parent'),
      }
    })


    console.debug(Date.now() - time,'selectPerHousehold', 'compressed targets');

    // this.target.load(targets_compressed); // this messes up the simplified targets

    this.target.unload();
    targets_compressed.forEach(taget=>this.target.setScopes(      
      taget.group_id,
      taget.scopes,
    )); 
    
    console.debug(Date.now() - time,'selectPerHousehold', 'targets selected');
  }

  ngAfterViewInit(): void {
    forEach(this.elementRef.nativeElement.querySelectorAll("div[translation-text]"), (el) => this.translation.set(el.id,  el.attributes["translation-text"].value))
  }    

  downloadStudentListExcelTpl(){
    return `assets/docs/${this.locale_id.slice(0,2)}/target_studentlist_format.xlsx`;
  }

  downloadFacultyListExcelTpl(){
    return `assets/docs/${this.locale_id.slice(0,2)}/target_facultylist_format.xlsx`;
  }
}
